import React, { useCallback, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";

/**
 * Draggable divider between the chat thread and the docked canvas.
 *
 * The canvas is measured from the right edge of the window, so the drag maths
 * stay correct no matter how wide the left rail is. Width is committed to the
 * store (which clamps and persists it) on every move — cheap, since it only
 * writes a single observable.
 */
const CanvasResizer = observer(() => {
  const draggingRef = useRef(false);

  const onMouseMove = useCallback(e => {
    if (!draggingRef.current) return;
    copilotStore.setCanvasWidth(window.innerWidth - e.clientX);
  }, []);

  const stopDragging = useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    // Restore text selection + cursor once the drag ends.
    document.body.classList.remove("copilotResizing");
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", stopDragging);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", stopDragging);
      document.body.classList.remove("copilotResizing");
    };
  }, [onMouseMove, stopDragging]);

  const startDragging = () => {
    draggingRef.current = true;
    // Suppress selection while dragging, or the whole thread highlights blue.
    document.body.classList.add("copilotResizing");
  };

  // Keyboard access: the divider is a real separator, so arrow keys move it.
  const onKeyDown = e => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const step = e.shiftKey ? 64 : 16;
    copilotStore.setCanvasWidth(
      copilotStore.canvasWidth + (e.key === "ArrowLeft" ? step : -step)
    );
  };

  return (
    <div
      className="copilotPage__resizer"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize panel"
      tabIndex={0}
      onMouseDown={startDragging}
      onKeyDown={onKeyDown}
    >
      <span className="copilotPage__resizerGrip" />
    </div>
  );
});

export default CanvasResizer;
